import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import ComingSoon from './ComingSoon';
interface CardProps {
  id: number;
  title: string;
  description: string;
  shortDescription?: string;
  image: string;
  category: string;
  client?: string;
  year?: number;
  videoUrl?: string;
  comingSoon?: boolean;
}
const Card = ({
  id,
  title,
  description,
  shortDescription,
  image,
  category,
  client = '',
  year,
  videoUrl,
  comingSoon
}: CardProps) => {
  const navigate = useNavigate();
  const slug = title.toLowerCase().replace(/\s+/g, '-');
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  const [isHovered, setIsHovered] = useState(false);
  const arrowRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [parallaxValues, setParallaxValues] = useState({
    x: 0,
    y: 0
  });
  const isRudelProject = title === 'Rüdel Social Media';
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (arrowRef.current) {
        const rect = arrowRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const maxOffset = 15;
        const offsetX = Math.min(Math.max((e.clientX - centerX) * 0.1, -maxOffset), maxOffset);
        const offsetY = Math.min(Math.max((e.clientY - centerY) * 0.1, -maxOffset), maxOffset);
        requestAnimationFrame(() => {
          setMousePosition({
            x: offsetX,
            y: offsetY
          });
        });
      }
      if (cardRef.current && isHovered) {
        const rect = cardRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const maxParallax = isRudelProject ? 15 : 10;
        const parallaxX = (e.clientX - centerX) / rect.width * maxParallax;
        const parallaxY = (e.clientY - centerY) / rect.height * maxParallax;
        setParallaxValues({
          x: parallaxX,
          y: parallaxY
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove, {
      passive: true
    });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isHovered, isRudelProject]);
  const handleCategoryClick = (e: React.MouseEvent, categoryToNavigate: string) => {
    e.preventDefault();
    e.stopPropagation();
    const path = categoryToNavigate.toLowerCase() === 'motion design' ? 'motion' : categoryToNavigate.toLowerCase();
    navigate(`/${path}`);
  };
  const getCategories = () => {
    if (typeof category === 'string') return [category];
    return category;
  };
  return <motion.div ref={cardRef} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} whileHover={{
    y: -5
  }} transition={{
    duration: 0.6,
    ease: 'easeOut'
  }} className={`project-card group relative bg-white rounded-3xl transition-all duration-500 ease-out
                shadow-sm hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] hover:shadow-indigo-500/10 overflow-hidden
                ${comingSoon ? 'pointer-events-none' : ''}`} style={{
    willChange: 'transform'
  }} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => {
    setIsHovered(false);
    setParallaxValues({
      x: 0,
      y: 0
    });
  }} data-id="element-49">
      <Link to={`/project/${slug}`} className="block" data-id="element-50">
        <div className={`project-thumbnail relative overflow-hidden rounded-t-[1.5rem] aspect-w-16 aspect-h-9`} data-id="element-51">
          <motion.div className="w-full h-full" whileHover={{
          scale: 1.15,
          transition: {
            duration: 0.8,
            ease: [0.19, 1.0, 0.22, 1.0]
          }
        }} data-id="element-52">
            <img src={`${image}?w=800&q=75`} alt={title} loading="lazy" className={`w-full h-full object-cover transition-all duration-700 ease-out
                ${title === 'Rüdell Social Media' ? 'object-top' : 'object-center'}`} data-id="element-53" />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-70 transition-opacity duration-500 group-hover:opacity-90" data-id="element-54" />
          <motion.div ref={arrowRef} className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-20" initial={{
          scale: 0.8
        }} whileHover={{
          scale: 1.1
        }} data-id="element-55">
            <motion.div className="bg-white/90 rounded-full p-3 shadow-lg" animate={{
            x: mousePosition.x,
            y: mousePosition.y
          }} transition={{
            type: 'spring',
            stiffness: 150,
            damping: 15,
            mass: 0.1
          }} data-id="element-56">
              <ArrowUpRight className="h-6 w-6 text-gray-800" data-id="element-57" />
            </motion.div>
          </motion.div>
        </div>
      </Link>
      <div className="project-info p-7" data-id="element-58">
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4" data-id="element-59">
          <div className="flex flex-wrap items-center gap-2" data-id="element-60">
            {Array.isArray(category) ? category.map((cat, index) => <motion.button key={index} onClick={e => handleCategoryClick(e, cat)} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-medium
                    backdrop-blur-sm transition-all duration-300
                    ${cat === 'Video' ? 'bg-indigo-50/80 text-indigo-600 hover:bg-indigo-100' : cat === 'Photo' ? 'bg-rose-50/80 text-rose-600 hover:bg-rose-100' : 'bg-teal-50/80 text-teal-600 hover:bg-teal-100'}`} data-id="element-61">
                  {cat === 'Video' ? 'Video' : cat === 'Photo' ? 'Foto' : 'Motion Design'}
                </motion.button>) : <motion.button onClick={e => handleCategoryClick(e, category)} whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className={`inline-flex items-center px-4 py-1.5 rounded-full text-xs font-medium
                  backdrop-blur-sm transition-all duration-300
                  ${category === 'Video' ? 'bg-indigo-50/80 text-indigo-600 hover:bg-indigo-100' : category === 'Photo' ? 'bg-rose-50/80 text-rose-600 hover:bg-rose-100' : 'bg-teal-50/80 text-teal-600 hover:bg-teal-100'}`} data-id="element-62">
                {category === 'Video' ? 'Video' : category === 'Photo' ? 'Foto' : 'Motion Design'}
              </motion.button>}
          </div>
          {year && <div className="flex-shrink-0" data-id="element-63">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100/60 text-gray-600" data-id="element-64">
                {year}
              </span>
            </div>}
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2 transition-colors duration-300 group-hover:text-gray-700" data-id="element-65">
          {title}
        </h3>
        {client && <p className="text-sm text-gray-500 mb-2" data-id="element-66">{client}</p>}
        {shortDescription && <p className="text-sm text-gray-600 line-clamp-2" data-id="element-67">
            {shortDescription}
          </p>}
      </div>
      {comingSoon && <ComingSoon data-id="element-68" />}
    </motion.div>;
};
export default Card;